import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import CompanyContext from '../../../auth/CompanyContext';
import Loading from '../../Loading/Loading';

const PortalRealizedGains = (props) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(props.companySettings?.CompanyPortalDatabase || '').trim() !== '';

    const { callback, onWidgetLoad, onAddCallback } = props;

    const getValues = useCallback((width) => {
        let col1 = 0, col2 = 0, graphHeight = 0;
        switch (width) {
            case 4:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 6:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 12:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            default:
        }
        return { col1, col2, graphHeight };
    }, []);

    const initialValues = getValues(props.width);
    const [data, setData] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [col1, setCol1] = useState(initialValues.col1);
    const [col2, setCol2] = useState(initialValues.col2);
    const [graphHeight, setGraphHeight] = useState(initialValues.graphHeight);
    const [styleSettings, setStyleSettings] = useState({});
    const [loading, setLoading] = useState(true);

    const refreshRef = useRef(() => { });
    const styleSettingsRef = useRef({});

    const toNumber = useCallback((val) => {
        const parsed = Number(val);
        return Number.isFinite(parsed) ? parsed : 0;
    }, []);

    const parseDate = useCallback((value, toUTCDateOnly = false) => {
        if (!value) return null;
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return null;
        if (!toUTCDateOnly) return parsed;
        return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
    }, []);

    const getPeriodStart = useCallback(() => {
        const period = (styleSettingsRef.current || {}).rgainDefaultPeriod;
        const date = new Date();

        if (period === 'Month to Date') return new Date(date.getFullYear(), date.getMonth(), 1);
        if (period === 'Quarter to Date') {
            const quarter = Math.floor(date.getMonth() / 3);
            return new Date(date.getFullYear(), quarter * 3, 1);
        }
        if (period === 'Year to Date') return new Date(date.getFullYear(), 0, 1);
        if (period === '6 Month') return new Date(date.getFullYear(), date.getMonth() - 6, date.getDate());
        if (period === '12 Month') return new Date(date.getFullYear() - 1, date.getMonth(), date.getDate());
        return null;
    }, []);

    const getStyleSettings = useCallback(async () => {
        if (!hasCompanyPortalDatabase) return;

        const styleNames = ['rgainDefaultPeriod'];
        const params = new URLSearchParams();
        styleNames.forEach(name => params.append('styleNames[]', name));

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/getStyleSettings?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();
            const nextStyleSettings = response.styleSettings || {};
            styleSettingsRef.current = nextStyleSettings;
            setStyleSettings(nextStyleSettings);
        } catch (err) {
            console.error(err);
            styleSettingsRef.current = {};
            setStyleSettings({});
        }
    }, [props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const getRGains = useCallback(async () => {
        if (!hasCompanyPortalDatabase) {
            setData(null);
            setLoading(false);
            return;
        }

        setLoading(true);

        const household = typeof props.selectedHousehold === 'object'
            ? props.selectedHousehold && props.selectedHousehold.household
            : (props.selectedHousehold || '');
        const from = getPeriodStart();
        const to = new Date();

        const params = new URLSearchParams({
            household,
            port: household,
            from: from ? from.toISOString() : '',
            to: to.toISOString()
        });

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/rgainsSnapshot?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();

            if (response.code !== 200 || !Array.isArray(response.data) || response.data.length === 0) {
                setData(null);
                setLoading(false);
                setFromDate(from);
                setToDate(to);
                return;
            }

            const row = response.data[0] || {};
            const shortTerm = toNumber(row.shortTerm);
            const longTerm = toNumber(row.longTerm);
            const percentRGL = toNumber(row.percentRGL);
            const rGainData = {
                shortTerm,
                longTerm,
                percentRGL,
                totalRGains: shortTerm + longTerm
            };

            const hasAnyValue = shortTerm !== 0 || longTerm !== 0 || percentRGL !== 0;
            const computedFromDate = from || parseDate(row.fromDate, true);

            setData(hasAnyValue ? rGainData : null);
            setLoading(false);
            setFromDate(computedFromDate);
            setToDate(to);
        } catch (err) {
            console.log(err);
            setData(null);
            setLoading(false);
            setFromDate(from);
            setToDate(to);
        }
    }, [props.selectedHousehold, props.idToken, companyCtx.company, getPeriodStart, toNumber, parseDate, hasCompanyPortalDatabase]);

    const onSubmit = useCallback(() => {
        getRGains();
    }, [getRGains]);

    const runRefresh = useCallback(() => {
        setLoading(true);
        onSubmit();
    }, [onSubmit]);

    useEffect(() => {
        refreshRef.current = runRefresh;
    }, [runRefresh]);

    useEffect(() => {
        let active = true;

        const init = async () => {
            await getStyleSettings();
            if (!active) return;

            callback((width) => {
                if (!active) return;
                const values = getValues(width);
                setGraphHeight(values.graphHeight);
                setCol1(values.col1);
                setCol2(values.col2);
            });

            if (onWidgetLoad) {
                onWidgetLoad();
                onAddCallback(() => {
                    refreshRef.current();
                });
            }
        };

        init();

        return () => {
            active = false;
        };
    }, [hasCompanyPortalDatabase]);

    const formatCurrency = (val) => {
        const num = toNumber(val);
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
    };

    const getDateHeader = () => {
        return (
            (fromDate ? fromDate.toLocaleDateString('en-US') : 'N/A')
            + ' to ' +
            (toDate ? toDate.toLocaleDateString('en-US') : 'N/A')
        );
    };

    void styleSettings;
    void col1;
    void col2;

    return (
        <Card className='w-100 m-0'>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h4>Realized Gains</h4>
                {props.icon}
            </CardHeader>
            <CardBody className='p-3'>
                {loading && <Loading />}
                {!loading && (
                    <>
                        <div className='rGainSnapshotHeader'>
                            <label>{getDateHeader()}</label>
                        </div>
                        {data ? (
                            <div style={{ minHeight: graphHeight + 'px' }}>
                                <table className='rGainSnapshot'>
                                    <tbody>
                                        <tr>
                                            <td className='totalRGainLabel'>
                                                <label style={{ fontWeight: 'bold' }}>Total Realized Gain/Loss</label>
                                            </td>
                                            <td className='totalRGainValue'>
                                                <label style={{ fontWeight: 'bold' }}>{formatCurrency(data.totalRGains)}</label>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table className='rGainSnapshot'>
                                    <tbody>
                                        <tr>
                                            <td className='rGainLabel'>
                                                <label>% RGL</label>
                                            </td>
                                            <td className='rGainValue'>
                                                <label>{(Math.round(data.percentRGL * 10000) / 100).toFixed(2)}%</label>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className='rGainLabel'>
                                                <label>Short-Term</label>
                                            </td>
                                            <td className='rGainValue'>
                                                <label>{formatCurrency(data.shortTerm)}</label>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className='rGainLabel'>
                                                <label>Long-Term</label>
                                            </td>
                                            <td className='rGainValue'>
                                                <label>{formatCurrency(data.longTerm)}</label>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className='widgetNoDataWithDate'>No transactions during the period</div>
                        )}
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default PortalRealizedGains;
